import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import axios from "axios";
import { useAuthDetails } from "../../Common/cookiesHelper";
import {
  BASE_URL,
  BUCKET_BASE_URL,
  FEATURE_ANNOUNCEMENTS_IMAGE_PATH,
  SHOW_FEATURE_ANNOUNCEMENTS,
  ADD_FEATURE_ANNOUNCEMENTS_VIEWED,
  QV_SESSION_LOGIN_ID_STORAGE_KEY,
} from "../../Constants/Config";
import { ciUrl } from "../../Constants/nodeCi";

/** Auto-close after 2 minutes if the user does not dismiss manually. */
const AUTO_DISMISS_MS = 2 * 60 * 1000;

/** Main merchant dashboard — SHOW_FEATURE_ANNOUNCEMENTS runs at most once per user per SPA session (first /dashboard visit). */
const DASHBOARD_PATH = "/dashboard";

/** Modal slide image viewport (px). */
const SLIDE_VIEWPORT_PX = 460;

/**
 * Square slide size: caps at SLIDE_VIEWPORT_PX and shrinks so it fits the device
 * (portrait/landscape). Uses vmin so width and height constraints stay balanced.
 */
const SLIDE_VIEWPORT_FIT_CSS = `min(${SLIDE_VIEWPORT_PX}px, calc(100vmin - 48px))`;

/**
 * Guards against repeat SHOW_FEATURE_ANNOUNCEMENTS calls (re-renders, tab focus, leaving and returning to /dashboard).
 * Cleared when `user_id` is absent (logout).
 */
let showFeatureAnnouncementsFetchedForUserId = null;

function normalizeShowAnnouncementsPayload(payload) {
  if (!payload) return [];
  const list =
    payload?.data ??
    payload?.feature_announcements ??
    payload?.feature_announcement_data ??
    payload?.announcements ??
    payload?.result ??
    (Array.isArray(payload) ? payload : []);
  return Array.isArray(list) ? list : [];
}

function getAnnouncementId(row) {
  return row?.id ?? row?.fa_id ?? row?.feature_announcement_id ?? row?.announcement_id;
}

/** `qv_session_login_id` in localStorage only (no cookie / legacy fallback). */
function getRawQvSessionLoginIdFromStorage() {
  try {
    const v = localStorage.getItem(QV_SESSION_LOGIN_ID_STORAGE_KEY);
    return v != null && String(v).trim() !== "" ? String(v).trim() : null;
  } catch {
    return null;
  }
}

function resolveSlideImageSrc(imageField) {
  if (imageField == null || String(imageField).trim() === "") return "";
  const s = String(imageField).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  const noLead = s.replace(/^\//, "");
  if (
    noLead.startsWith("upload/") ||
    noLead.includes("feature-announcements/")
  ) {
    return `${BUCKET_BASE_URL}${noLead}`;
  }
  return `${BUCKET_BASE_URL}${FEATURE_ANNOUNCEMENTS_IMAGE_PATH}${noLead}`;
}

/**
 * Feature announcement promo modal (API-driven). Shown when show_feature_announcements returns rows.
 * Backdrop, Escape, or auto after 2 minutes. Slides: prev/next; no auto-slide.
 */
export default function MerchantTimesheetPromoBanner() {
  const location = useLocation();
  const { LoginGetDashBoardRecordJson, userTypeData, userTypeDataLog } =
    useAuthDetails();

  const logSessionUserId = userTypeDataLog?.session_login_id;
  const user_id =
    logSessionUserId != null && String(logSessionUserId).trim() !== ""
      ? String(logSessionUserId).trim()
      : null;
  const merchant_id = LoginGetDashBoardRecordJson?.data?.merchant_id;
  const token = userTypeData?.token;

  const [promoSlides, setPromoSlides] = useState([]);
  const [fetchState, setFetchState] = useState("idle");
  const [eligible, setEligible] = useState(false);
  const [open, setOpen] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const hideTimerRef = useRef(null);
  const dismissRef = useRef(() => {});
  /** Feature announcement ids already reported via ADD_FEATURE_ANNOUNCEMENTS_VIEWED this session. */
  const viewedFaIdsRef = useRef(new Set());

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  /**
   * Records a single announcement as viewed. Idempotent per fa_id for this modal session.
   * Called when the user sees a slide (initial first slide, or after next/prev/dot navigation).
   */
  const recordAnnouncementViewedIfNeeded = useCallback(
    async (faId) => {
      const id = faId != null ? String(faId).trim() : "";
      if (!id || !user_id || !token) return;
      if (viewedFaIdsRef.current.has(id)) return;
      viewedFaIdsRef.current.add(id);
      const Viewdata = {
        fa_id: id,
        user_id,
        merchant_id: merchant_id,
      };
      const { token: bearerToken, ...dataNew } = Viewdata;
      try {
        await axios.post(BASE_URL + ADD_FEATURE_ANNOUNCEMENTS_VIEWED, dataNew, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      } catch {
        viewedFaIdsRef.current.delete(id);
      }
    },
    [user_id, token, merchant_id]
  );

  const dismiss = useCallback(() => {
    clearHideTimer();
    setOpen(false);
    setEligible(false);
  }, []);

  dismissRef.current = dismiss;

  const goToNewTimesheet = useCallback(() => {
    dismiss();
  }, [dismiss]);

  const slideCount = promoSlides.length;
  const canPrev = slideIndex > 0;
  const canNext = slideIndex < slideCount - 1;

  const goPrev = useCallback((e) => {
    e?.stopPropagation();
    setSlideIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(
    (e) => {
      e?.stopPropagation();
      setSlideIndex((i) => Math.min(slideCount - 1, i + 1));
    },
    [slideCount]
  );

  const goToSlide = useCallback((e, index) => {
    e?.stopPropagation();
    if (index < 0 || index >= slideCount) return;
    setSlideIndex(index);
  }, [slideCount]);

  useEffect(() => {
    if (!merchant_id || !user_id || !token) {
      setPromoSlides([]);
      setFetchState("idle");
      setEligible(false);
      setOpen(false);
      viewedFaIdsRef.current = new Set();
      if (!user_id) {
        showFeatureAnnouncementsFetchedForUserId = null;
      }
      return;
    }

    if (location.pathname !== DASHBOARD_PATH) {
      return;
    }

    if (showFeatureAnnouncementsFetchedForUserId === String(user_id)) {
      return;
    }

    if (!getRawQvSessionLoginIdFromStorage()) {
      return;
    }

    let cancelled = false;
    setFetchState("loading");

    const Showdata = {
      id: "",
      user_id,
      is_show: 1,
      merchant_id: merchant_id,
    };
    const { token: bearerToken, ...dataNew } = Showdata;
    axios
      .post(ciUrl(SHOW_FEATURE_ANNOUNCEMENTS), dataNew, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (cancelled) return;
        showFeatureAnnouncementsFetchedForUserId = String(user_id);
        const rows = normalizeShowAnnouncementsPayload(res.data);
        const slides = rows
          .map((row) => {
            const id = getAnnouncementId(row);
            const imgRaw =
              row?.image ??
              row?.banner_image ??
              (Array.isArray(row?.banner_images) && row.banner_images[0]) ??
              (Array.isArray(row?.images) && row.images[0]);
            const src = resolveSlideImageSrc(
              imgRaw != null ? String(imgRaw) : ""
            );
            const desc =
              row?.description ?? row?.banner_description ?? row?.desc ?? "";
            return {
              id,
              src,
              alt:
                desc && String(desc).trim()
                  ? String(desc).trim()
                  : "Feature announcement",
            };
          })
          .filter((s) => s.id != null && s.id !== "" && s.src);

        if (slides.length > 0) {
          setPromoSlides(slides);
          setEligible(true);
          setOpen(true);
          setSlideIndex(0);
        } else {
          setPromoSlides([]);
          setEligible(false);
          setOpen(false);
        }
        setFetchState("done");
      })
      .catch(() => {
        if (cancelled) return;
        showFeatureAnnouncementsFetchedForUserId = String(user_id);
        setPromoSlides([]);
        setEligible(false);
        setOpen(false);
        setFetchState("error");
      });

    return () => {
      cancelled = true;
    };
    // Omit userTypeData (new object each render); spread uses latest closure when effect runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    merchant_id,
    user_id,
    token,
    location.pathname,
  ]);

  useEffect(() => {
    if (eligible !== true || !open) return;
    hideTimerRef.current = setTimeout(() => {
      dismissRef.current();
    }, AUTO_DISMISS_MS);
    return () => clearHideTimer();
  }, [eligible, open]);

  /** First visible slide on open, and each slide only after user navigates to it (next/prev/dots). */
  useEffect(() => {
    if (!open || eligible !== true) return;
    const slide = promoSlides[slideIndex];
    const id = slide?.id;
    if (id == null || id === "") return;
    void recordAnnouncementViewedIfNeeded(id);
  }, [
    open,
    eligible,
    slideIndex,
    promoSlides,
    recordAnnouncementViewedIfNeeded,
  ]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft" && canPrev) {
        e.preventDefault();
        goPrev(e);
      } else if (e.key === "ArrowRight" && canNext) {
        e.preventDefault();
        goNext(e);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, canPrev, canNext, goPrev, goNext]);

  if (fetchState === "loading" || fetchState === "idle") return null;
  if (fetchState === "error") return null;
  if (eligible !== true || !open || slideCount === 0) return null;

  const current = promoSlides[slideIndex];

  return (
    <Modal
      open={open}
      onClose={() => {
        dismiss();
      }}
      BackdropProps={{
        sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" },
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        outline: "none",
        zIndex: (theme) => theme.zIndex.modal + 2,
        width: "100%",
        height: "100vh",
        boxSizing: "border-box",
        "@supports (height: 100dvh)": {
          height: "100dvh",
        },
        pt: `max(16px, env(safe-area-inset-top, 0px))`,
        pb: `max(16px, env(safe-area-inset-bottom, 0px))`,
        pl: `max(16px, env(safe-area-inset-left, 0px))`,
        pr: `max(16px, env(safe-area-inset-right, 0px))`,
        overflow: "auto",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "relative",
          width: SLIDE_VIEWPORT_FIT_CSS,
          height: SLIDE_VIEWPORT_FIT_CSS,
          maxWidth: "100%",
          maxHeight: "100%",
          margin: "auto",
          flexShrink: 0,
          outline: "none",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "none",
        }}
      >
        {slideCount > 1 && (
          <>
            <IconButton
              type="button"
              aria-label="Previous slide"
              disabled={!canPrev}
              onClick={goPrev}
              sx={{
                position: "absolute",
                left: 4,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 3,
                border: "1px solid var(--qv-bd-000000, #000)",
                bgcolor: "var(--qv-bg-a7aaae, #a7aaae)",
                color: "var(--qv-tx-1469f9, #1469f9)",
                boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                "&:hover": {
                  bgcolor: "var(--qv-bg-d1d5db, #d1d5db)",
                  border: "1px solid var(--qv-bd-000000, #000)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                },
                "&.Mui-disabled": {
                  opacity: 0.45,
                  bgcolor: "var(--qv-bg-e5e7eb, #e5e7eb)",
                  border: "1px solid var(--qv-bd-000000, #000)",
                },
              }}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              type="button"
              aria-label="Next slide"
              disabled={!canNext}
              onClick={goNext}
              sx={{
                position: "absolute",
                right: 4,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 3,
                border: "1px solid var(--qv-bd-000000, #000)",
                bgcolor: "var(--qv-bg-a7aaae, #a7aaae)",
                color: "var(--qv-tx-1469f9, #1469f9)",
                boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                "&:hover": {
                  bgcolor: "var(--qv-bg-d1d5db, #d1d5db)",
                  border: "1px solid var(--qv-bd-000000, #000)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                },
                "&.Mui-disabled": {
                  opacity: 0.45,
                  bgcolor: "var(--qv-bg-e5e7eb, #e5e7eb)",
                  border: "1px solid var(--qv-bd-000000, #000)",
                },
              }}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        )}

        <Box
          component="img"
          key={slideIndex}
          src={current.src}
          alt={current.alt}
          onClick={(e) => {
            e.stopPropagation();
            goToNewTimesheet();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              goToNewTimesheet();
            }
          }}
          role="button"
          tabIndex={0}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "contain",
            verticalAlign: "top",
            cursor: "pointer",
            boxShadow: "none",
          }}
        />

        {slideCount > 1 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              gap: 0.75,
              zIndex: 3,
              pointerEvents: "none",
              "& > *": { pointerEvents: "auto" },
            }}
          >
            {promoSlides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === slideIndex ? "true" : undefined}
                onClick={(e) => goToSlide(e, i)}
                style={{
                  width: i === slideIndex ? 22 : 8,
                  height: 8,
                  borderRadius: 999,
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  background:
                    i === slideIndex
                      ? "var(--qv-bg-a255x255x255x950, rgba(255,255,255,0.95))"
                      : "var(--qv-bg-a255x255x255x450, rgba(255,255,255,0.45))",
                  boxShadow: "none",
                  transition: "width 0.2s ease, background 0.2s ease",
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Modal>
  );
}
